import './ScalingDecisionRead.scss';
import { ReactElement } from 'react';
import { Placement } from '../../../Components/designConstants';
import { InfoTooltip } from '../../../Components/InfoTooltip';
import { formatSpend } from '../forecasting-constants';
import { breakevenSpend, lastHistoricalIndex, marginalPerDollar } from './scaling-math';
import { ScalingCurveParams, ScalingCurvePoint } from './scalingData';

interface ScalingDecisionReadProps {
  // Revenue outcome curve — this read is ROAS-specific (a volume curve has no 1x break-even).
  curve: ScalingCurvePoint[];
  // The fit's a/b, for exact marginal + break-even math (absent on older runs).
  params?: ScalingCurveParams;
}

interface DecisionTileProps {
  label: string;
  value: string;
  hint: string;
  tooltip: string;
}

const formatRoas = (value: number | null): string =>
  value === null ? '--' : `${value.toFixed(2)}x`;

const DecisionTile = ({ label, value, hint, tooltip }: DecisionTileProps): ReactElement => (
  <div className='scaling-decision-tile'>
    <div className='scaling-decision-tile-label'>
      <span>{label}</span>
      <InfoTooltip placement={Placement.BOTTOM}>{tooltip}</InfoTooltip>
    </div>
    <div className='scaling-decision-tile-value'>{value}</div>
    <div className='scaling-decision-tile-hint'>{hint}</div>
  </div>
);

// Leads the tab with the decision-relevant read: marginal ROAS at the current spend (what the next
// dollar returns from where you are today) and the break-even spend (where marginal ROAS falls to
// 1x). Deliberately NOT average ROAS, which overstates the next-dollar return under diminishing
// returns. The full marginal curve lives in the chart below; this strip is the headline.
export const ScalingDecisionRead = ({ curve, params }: ScalingDecisionReadProps): ReactElement => {
  const marginalNow = marginalPerDollar(curve, lastHistoricalIndex(curve), params);
  const breakeven = breakevenSpend(curve, params);

  return (
    <div className='scaling-decision-read'>
      <div className='scaling-decision-title'>Should you scale?</div>
      <div className='scaling-decision-tiles'>
        <DecisionTile
          label='Marginal ROAS at max historical spend'
          value={formatRoas(marginalNow)}
          hint='Return on the next $1 — the curve slope, not the average'
          tooltip='The fitted revenue curve’s slope (Δrevenue/Δspend) at your max historical spend. Above 1x, the next dollar still pays back more than a dollar; below 1x it does not. This is the decision-relevant read — average ROAS (revenue ÷ spend) overstates it under diminishing returns.'
        />
        <DecisionTile
          label='Break-even spend'
          value={breakeven === null ? 'Beyond modeled range' : formatSpend(breakeven)}
          hint={
            breakeven === null
              ? 'Marginal ROAS stays above 1x across the modeled range'
              : 'Weekly spend where marginal ROAS falls to 1x'
          }
          tooltip='The weekly spend level where the next dollar stops paying back — marginal ROAS crosses 1x. Scaling past this point buys revenue at a loss on the margin. “Beyond modeled range” means the curve never crosses 1x within the spend range we modeled.'
        />
      </div>
    </div>
  );
};
